// Listas de contatos (/contact-lists) — os públicos usados nas campanhas.
// Mockup: "Listas de contatos (/contact-lists) + modal".
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import InputBase from "@material-ui/core/InputBase";
import Tooltip from "@material-ui/core/Tooltip";
import SearchIcon from "@material-ui/icons/Search";
import AddIcon from "@material-ui/icons/Add";
import EditIcon from "@material-ui/icons/Edit";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import GroupsIcon from "@mui/icons-material/GroupsOutlined";
import ListAltIcon from "@material-ui/icons/ListAlt";
import GetAppIcon from "@material-ui/icons/GetApp";
import { makeStyles, alpha } from "@material-ui/core/styles";
import moment from "moment";

import MainContainer from "../../components/MainContainer";
import CampaignsNav from "../../components/CampaignsNav";
import ContactListDialog from "../../components/ContactListDialog";
import AerosConfirmModal from "../../components/AerosConfirmModal";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import useCampaignPageStyles from "../Campaigns/campaignPageStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";

const GRID = "minmax(0,1fr) 130px 130px 130px";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTLISTS") {
    const novas = [];
    action.payload.forEach((lista) => {
      const i = state.findIndex((l) => l.id === lista.id);
      if (i !== -1) state[i] = lista;
      else novas.push(lista);
    });
    return [...state, ...novas];
  }
  if (action.type === "UPDATE_CONTACTLIST") {
    const lista = action.payload;
    const i = state.findIndex((l) => l.id === lista.id);
    if (i !== -1) {
      state[i] = lista;
      return [...state];
    }
    return [lista, ...state];
  }
  if (action.type === "DELETE_CONTACTLIST") {
    return state.filter((l) => l.id !== action.payload);
  }
  if (action.type === "RESET") return [];
  return state;
};

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  return {
    headerSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      marginTop: 2,
    },
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 14,
      overflow: "hidden",
    },
    rowHead: {
      display: "grid",
      gridTemplateColumns: GRID,
      gap: 10,
      padding: "10px 16px",
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      borderBottom: `1px solid ${border}`,
    },
    row: {
      display: "grid",
      gridTemplateColumns: GRID,
      gap: 10,
      padding: "11px 16px",
      borderBottom: `1px solid ${
        isLight ? theme.palette.divider : "rgba(255,255,255,0.05)"
      }`,
      alignItems: "center",
      fontSize: 12.5,
      cursor: "pointer",
      "&:hover": {
        backgroundColor: isLight
          ? "rgba(15,20,40,0.02)"
          : "rgba(255,255,255,0.02)",
      },
    },
    nomeCell: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
    tile: {
      width: 30,
      height: 30,
      borderRadius: 9,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      backgroundColor: alpha(AEROS.defaultPrimary, 0.14),
      color: stateText(theme, "#C08BFF", "purple"),
      "& .MuiSvgIcon-root": { fontSize: 16 },
    },
    nome: {
      fontWeight: 600,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    num: {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    data: {
      textAlign: "right",
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.secondary,
    },
    acoes: { display: "flex", gap: 2, justifyContent: "flex-end" },
    vazio: {
      padding: "28px 16px",
      textAlign: "center",
      fontSize: 12.5,
      color: theme.palette.aeros.textTertiary,
    },
  };
});

const ContactLists = () => {
  const c = useCampaignPageStyles();
  const classes = useStyles();
  const history = useHistory();

  const [listas, dispatch] = useReducer(reducer, []);
  const [termo, setTermo] = useState("");
  const [pagina, setPagina] = useState(1);
  const [temMais, setTemMais] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [modalAberto, setModalAberto] = useState(false);
  const [selecionada, setSelecionada] = useState(null);
  const [excluindo, setExcluindo] = useState(null);
  const [versao, setVersao] = useState(0);

  const recarrega = useCallback(() => {
    dispatch({ type: "RESET" });
    setVersao((v) => v + 1);
  }, []);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPagina(1);
  }, [termo]);

  useEffect(() => {
    setCarregando(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/contact-lists/", {
          params: { searchParam: termo, pageNumber: pagina },
        });
        dispatch({ type: "LOAD_CONTACTLISTS", payload: data.records });
        setTemMais(data.hasMore);
      } catch (err) {
        toastError(err);
      }
      setCarregando(false);
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [termo, pagina, versao]);

  const handleExcluir = async () => {
    if (!excluindo) return;
    try {
      await api.delete(`/contact-lists/${excluindo.id}`);
      toast.success(i18n.t("contactLists.toasts.deleted"));
      dispatch({ type: "DELETE_CONTACTLIST", payload: excluindo.id });
    } catch (err) {
      toastError(err);
    }
    setExcluindo(null);
  };

  const handleBaixar = async (lista) => {
    // Baixa o estado ATUAL da lista, gerado na hora pelo backend — não é a
    // planilha exemplo, que segue no diálogo de importação.
    try {
      const { data } = await api.get(`/contact-lists/${lista.id}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `lista-${lista.name || lista.id}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const handleScroll = (e) => {
    if (!temMais || carregando) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      setPagina((p) => p + 1);
    }
  };

  const abre = (lista) => history.push(`/contact-lists/${lista.id}/contacts`);

  const paraCada = (evento, fn) => {
    evento.stopPropagation();
    fn();
  };

  return (
    <MainContainer>
      <ContactListDialog
        open={modalAberto}
        onClose={() => {
          setModalAberto(false);
          setSelecionada(null);
          // O diálogo não devolve o registro salvo; recarrega a página atual
          // para a linha nova/renomeada aparecer.
          recarrega();
        }}
        aria-labelledby="form-dialog-title"
        contactListId={selecionada?.id}
      />
      <AerosConfirmModal
        open={Boolean(excluindo)}
        onClose={() => setExcluindo(null)}
        onConfirm={handleExcluir}
        icon={<DeleteOutlineIcon />}
        tone="danger"
        title={i18n.t("aeros.contactLists.confirmTitle", {
          name: excluindo?.name || "",
        })}
        description={i18n.t("aeros.contactLists.confirmMessage")}
        confirmLabel={i18n.t("aeros.contactLists.delete")}
      />

      <div className={c.pageHeader}>
        <div>
          <div className={c.pageTitle}>
            {i18n.t("aeros.contactLists.title")}{" "}
            <span className={c.pageTitleCount}>· {listas.length}</span>
          </div>
          <div className={classes.headerSubtitle}>
            {i18n.t("aeros.contactLists.subtitle")}
          </div>
        </div>
        <div className={c.headerActions}>
          <InputBase
            className={c.searchPill}
            placeholder={i18n.t("aeros.contactLists.search")}
            type="search"
            value={termo}
            onChange={(e) => setTermo(e.target.value)}
            startAdornment={<SearchIcon />}
            inputProps={{ "aria-label": i18n.t("aeros.contactLists.search") }}
          />
          <Button
            variant="contained"
            color="primary"
            className={c.gradientButton}
            startIcon={<AddIcon />}
            onClick={() => {
              setSelecionada(null);
              setModalAberto(true);
            }}
          >
            {i18n.t("aeros.contactLists.newList")}
          </Button>
        </div>
      </div>

      <div className={c.pageBody} onScroll={handleScroll}>
        <CampaignsNav />
        <div className={classes.card}>
          <div className={classes.rowHead}>
            <div>{i18n.t("aeros.contactLists.name")}</div>
            <div style={{ textAlign: "right" }}>
              {i18n.t("aeros.contactLists.contacts")}
            </div>
            <div style={{ textAlign: "right" }}>
              {i18n.t("aeros.contactLists.createdAt")}
            </div>
            <div style={{ textAlign: "right" }}>
              {i18n.t("aeros.contactLists.actions")}
            </div>
          </div>

          {carregando && listas.length === 0 ? (
            <div className={classes.vazio}>
              {i18n.t("aeros.contactLists.loading")}
            </div>
          ) : listas.length === 0 ? (
            <div className={classes.vazio}>
              {termo
                ? i18n.t("aeros.contactLists.emptySearch")
                : i18n.t("aeros.contactLists.empty")}
            </div>
          ) : (
            listas.map((lista) => (
              <div
                className={classes.row}
                key={lista.id}
                onClick={() => abre(lista)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter") abre(lista);
                }}
              >
                <div className={classes.nomeCell}>
                  <div className={classes.tile}>
                    <ListAltIcon />
                  </div>
                  <span className={classes.nome} title={lista.name}>
                    {lista.name}
                  </span>
                </div>
                <div className={classes.num}>
                  {Number(lista.contactsCount || 0).toLocaleString("pt-BR")}
                </div>
                <div className={classes.data}>
                  {lista.createdAt
                    ? moment(lista.createdAt).format("DD/MM/YYYY")
                    : "—"}
                </div>
                <div className={classes.acoes}>
                  <Tooltip title={i18n.t("aeros.contactLists.openContacts")}>
                    <IconButton
                      className={c.rowActionButton}
                      aria-label={i18n.t("aeros.contactLists.openContacts")}
                      onClick={(e) => paraCada(e, () => abre(lista))}
                    >
                      <GroupsIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("aeros.contactLists.download")}>
                    <IconButton
                      className={c.rowActionButton}
                      aria-label={i18n.t("aeros.contactLists.download")}
                      onClick={(e) => paraCada(e, () => handleBaixar(lista))}
                    >
                      <GetAppIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("aeros.contactLists.edit")}>
                    <IconButton
                      className={c.rowActionButton}
                      aria-label={i18n.t("aeros.contactLists.edit")}
                      onClick={(e) =>
                        paraCada(e, () => {
                          setSelecionada(lista);
                          setModalAberto(true);
                        })
                      }
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={i18n.t("aeros.contactLists.delete")}>
                    <IconButton
                      className={`${c.rowActionButton} ${c.rowActionButtonDanger}`}
                      aria-label={i18n.t("aeros.contactLists.delete")}
                      onClick={(e) => paraCada(e, () => setExcluindo(lista))}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </MainContainer>
  );
};

export default ContactLists;
